import { useEffect, useState } from "react";
import api, { usd, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Network, BookOpen, FileSignature, ShieldAlert, GraduationCap, ClipboardCheck, MessageCircleQuestion, FileBarChart, Send, CheckCircle2, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

const FEATURES = [
  [Network, "Org chart & manager access", "Live reporting lines, team views and per-manager permissions."],
  [BookOpen, "Policies & handbook", "AI-drafted policies, a branded handbook PDF and e-sign tracking."],
  [FileSignature, "AI HR letters", "Offer, warning, PIP and termination letters in seconds."],
  [ShieldAlert, "Compliance dashboard", "W-4, I-9, ID expirations and acknowledgements in one place."],
  [GraduationCap, "Training & certificates", "Assign courses, auto-remind, and file certificates for audits."],
  [ClipboardCheck, "HR health assessment", "20-point risk score with a prioritised fix-it list and trend."],
  [MessageCircleQuestion, "Ask an HR expert", "Instant AI HR advisor, escalate to your RightPay HR team."],
  [FileBarChart, "HR reports", "EEO-1, VETS-4212, new-hire and retirement filings ready to go."],
];

export function HRUpsellCard() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/my-modules").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d || d.modules?.hr || !d.catalog?.length) return null;
  const hr = d.catalog.find((m) => m.key === "hr");
  if (!hr) return null;
  const requested = d.requested.includes("hr");
  const request = async () => {
    setBusy(true);
    try { const { data } = await api.post("/my-modules/request", { module: "hr", note: "Requested from the HR Center preview card" }); toast.success(data.message); setD({ ...d, requested: [...d.requested, "hr"] }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Card className="p-0 overflow-hidden border-primary/30" data-testid="hr-upsell-card">
      <div className="bg-[#0b1a3a] text-white p-5 sm:p-6 flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-xl">
          <Badge className="bg-blue-500/20 text-blue-200 border-blue-400/30 mb-2"><Sparkles className="w-3 h-3 mr-1" /> Add-on</Badge>
          <h2 className="font-display text-lg sm:text-xl font-bold">Unlock HR Center for your team</h2>
          <p className="text-sm text-slate-300 mt-1">Everything below is one click away — your RightPay team enables it and it appears in your sidebar the same day.</p>
        </div>
        <div className="text-right space-y-2">
          {hr.fee > 0 && <div className="font-mono-num text-2xl font-bold" data-testid="hr-upsell-fee">{usd(hr.fee)}<span className="text-sm font-normal text-slate-300">/mo</span></div>}
          {requested
            ? <Badge variant="outline" className="text-emerald-300 border-emerald-400/40" data-testid="hr-upsell-requested"><CheckCircle2 className="w-3 h-3 mr-1" /> Requested — we'll be in touch</Badge>
            : <Button data-testid="hr-upsell-request" disabled={busy} onClick={request} className="bg-blue-600 hover:bg-blue-500">{busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />} Request HR Center</Button>}
        </div>
      </div>
      <ul className="grid sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 divide-border">
        {FEATURES.map(([Icon, t, s], i) => (
          <li key={t} className={`p-4 flex gap-3 ${i % 4 !== 3 ? "lg:border-r" : ""} ${i < 4 ? "lg:border-b" : ""} border-border`} data-testid={`hr-upsell-feature-${i}`}>
            <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0"><Icon className="w-4 h-4" /></div>
            <div><div className="text-sm font-medium">{t}</div><div className="text-xs text-muted-foreground mt-0.5">{s}</div></div>
          </li>))}
      </ul>
    </Card>
  );
}
